import { CopyFIcon, Icon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Controller, Input, Stack } from '@prophecy/ui-v3';
import { StyledHelpText, StyledLabel } from '@prophecy/ui-v3/Input/styled';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { useUser } from 'frontend/core/src/context/user';
import { setConfigureSSO } from 'frontend/core/src/data/apis/api';
import { AuthTypes } from 'frontend/core/src/data/apis/types';
import React, { useState } from 'react';
import styled from 'styled-components';

import { ComponentProps } from './types';

const StyledInput = styled(Input)`
  ${Icon} {
    cursor: pointer;
  }
`;

const StyledButton = styled(Button)`
  align-self: start;
` as unknown as typeof Button;
export const SCIMField = ({
  control,
  errors,
  hasSCIMToken,
  isSAML,
  isAzure
}: ComponentProps & {
  isSAML?: boolean;
  isAzure?: boolean;
}) => {
  const email = useUser()?.user?.email;
  const [loading, setLoading] = useState(false);

  if (!hasSCIMToken && !isSAML && !isAzure) return null;

  const generateSCIMToken = async (authType?: AuthTypes.azuread | AuthTypes.saml) => {
    setLoading(true);
    const promise = setConfigureSSO({ identifier: email, type: authType }).finally(() => setLoading(false));
    return (await promise)?.scimToken;
  };

  return (
    <Controller
      render={({ field }) => (
        <Stack gap={theme.spaces.x4}>
          {hasSCIMToken ? (
            <StyledInput
              disabled
              required={true}
              {...field}
              label={
                'SCIM Token (Your token was generated successfully. Use it on configuration platform to complete the set up.)'
              }
              icon={
                <CopyFIcon
                  type='default'
                  color={theme.colors.gray500}
                  onClick={async () => {
                    await navigator.clipboard.writeText(field.value);
                    toast.success({
                      content: 'SCIM Token copied to clipboard',
                      duration: 2000
                    });
                  }}
                />
              }
              data-test-id='prophecy-managed-scim-token-input'
            />
          ) : (
            <Stack gap={theme.spaces.x6}>
              <StyledLabel>SCIM Token</StyledLabel>
              <StyledButton
                variant='primary'
                loading={loading}
                disabled={loading}
                onClick={() =>
                  generateSCIMToken(isAzure ? AuthTypes.azuread : isSAML ? AuthTypes.saml : undefined).then((value) =>
                    field.onChange(value)
                  )
                }
                data-test-id='user-settings-prophecy-managed-generate-scim-token-button'>
                {loading ? 'Generating' : 'Generate'} SCIM Token
              </StyledButton>
            </Stack>
          )}
          {errors?.scimToken?.message && <StyledHelpText variant='error'>{errors?.scimToken?.message}</StyledHelpText>}
        </Stack>
      )}
      control={control}
      name='scimToken'
      rules={{
        required: isSAML || isAzure ? 'SCIM Token is required' : undefined
      }}
    />
  );
};

export default function ProphecyManaged(props: ComponentProps & { isSAML?: boolean }) {
  const { control, errors, isOktaConfigured, isSAML, isSCIMEnabled } = props;
  /*const userPromptUI = (
    <Controller
      render={({ field }) => (
        <UserPrompt
          hasPrompt={!props.isSCIMEnabled || props.isSCIMV1}
          errorMsg={errors?.usernamePrompt?.message}
          value={field.value}
          onChange={field.onChange}
        />
      )}
      control={control}
      name='usernamePrompt'
    />
  );*/
  if (!isSCIMEnabled) return null;

  return (
    <Stack gap={theme.spaces.x24} padding={isSAML ? undefined : `0 ${theme.spaces.x8} 0 ${theme.spaces.x4}`}>
      <SCIMField {...props} />
      <Controller
        render={({ field }) => (
          <Input
            label='Organization ID'
            {...field}
            disabled={isOktaConfigured}
            variant={errors?.orgID ? 'error' : 'primary'}
            helpText={errors?.orgID?.message}
            data-test-id='prophecy-managed-organization-id-input'
          />
        )}
        control={control}
        name='orgID'
        rules={{
          required: 'Organization ID is required'
        }}
      />
      <Controller
        render={({ field }) => (
          <Input
            label='Team Name'
            {...field}
            disabled={isOktaConfigured}
            variant={errors?.orgWideTeamName ? 'error' : 'primary'}
            helpText={errors?.orgWideTeamName?.message}
            data-test-id='prophecy-managed-team-name-input'
          />
        )}
        control={control}
        name='orgWideTeamName'
        rules={{
          required: 'Team name is required'
        }}
      />
    </Stack>
  );
}
